import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { X, Plus } from "lucide-react";
import { useClasses } from "@/contexts/ClassesContext";

// Fallback defaults (used before context loads); DB is source of truth once logged in.
const CLASS_COLORS = {
  Kardiomegali: "#f43f5e",
  Pneumonia: "#f97316",
  "Tb paru": "#eab308",
  Nodul: "#a855f7",
  normal: "#10b981",
};

const CLASSES = ["Kardiomegali", "Nodul", "Pneumonia", "Tb paru", "normal"];

function boxColor(cls, conf, colorMap = CLASS_COLORS) {
  if (colorMap[cls]) return colorMap[cls];
  return conf >= 0.7 ? "#f43f5e" : conf >= 0.4 ? "#f59e0b" : "#818cf8";
}

/**
 * Interactive bounding-box editor.
 * - View mode: static SVG overlays (like BoundingBoxOverlay).
 * - Edit mode: boxes are draggable, resizable, deletable; can add new boxes by
 *   click-drag on empty area. Box class is editable via findings panel.
 *
 * Coord system: predictions use image-native pixel coords with (x, y) being
 * the CENTER of the box. We store internally as {x, y, width, height, class, confidence}.
 */
export default function InteractiveBoxEditor({
  imageSrc,
  imageWidth,
  imageHeight,
  boxes,
  setBoxes,
  editable = false,
  hoveredIndex = null,
  onHover,
  testId = "box-editor",
}) {
  const { classColors: dynamicColors } = useClasses();
  const colorMap = useMemo(
    () => ({ ...CLASS_COLORS, ...dynamicColors }),
    [dynamicColors]
  );
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [dragState, setDragState] = useState(null); // {type, index, startX, startY, orig}
  const [drawState, setDrawState] = useState(null); // {startX, startY, curX, curY}
  const [addMode, setAddMode] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver(() => {
      setDisplaySize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Convert client (mouse) coordinates → image (native) coordinates
  const toImageCoords = useCallback(
    (clientX, clientY) => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const rect = svg.getBoundingClientRect();
      const scaleX = imageWidth / rect.width;
      const scaleY = imageHeight / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },
    [imageWidth, imageHeight]
  );

  // --- drag handlers ---
  const onMouseDown = (e, index, type) => {
    if (!editable) return;
    e.stopPropagation();
    const { x, y } = toImageCoords(e.clientX, e.clientY);
    setDragState({
      type,
      index,
      startX: x,
      startY: y,
      orig: { ...boxes[index] },
    });
  };

  const onMouseMove = useCallback(
    (e) => {
      if (dragState) {
        const { x, y } = toImageCoords(e.clientX, e.clientY);
        const dx = x - dragState.startX;
        const dy = y - dragState.startY;
        const orig = dragState.orig;
        const next = [...boxes];
        if (dragState.type === "move") {
          next[dragState.index] = {
            ...orig,
            x: clamp(orig.x + dx, orig.width / 2, imageWidth - orig.width / 2),
            y: clamp(orig.y + dy, orig.height / 2, imageHeight - orig.height / 2),
          };
        } else if (dragState.type === "resize-se") {
          const newW = Math.max(20, orig.width + dx * 2);
          const newH = Math.max(20, orig.height + dy * 2);
          next[dragState.index] = { ...orig, width: newW, height: newH };
        }
        setBoxes(next);
      } else if (drawState) {
        const { x, y } = toImageCoords(e.clientX, e.clientY);
        setDrawState({ ...drawState, curX: x, curY: y });
      }
    },
    [dragState, drawState, boxes, imageWidth, imageHeight, setBoxes, toImageCoords]
  );

  const onMouseUp = useCallback(() => {
    if (drawState) {
      const w = Math.abs(drawState.curX - drawState.startX);
      const h = Math.abs(drawState.curY - drawState.startY);
      if (w > 20 && h > 20) {
        const cx = (drawState.startX + drawState.curX) / 2;
        const cy = (drawState.startY + drawState.curY) / 2;
        setBoxes([
          ...boxes,
          {
            class: "Kardiomegali",
            confidence: 1.0,
            x: cx,
            y: cy,
            width: w,
            height: h,
            source: "radiologist",
          },
        ]);
      }
      setDrawState(null);
      setAddMode(false);
    }
    setDragState(null);
  }, [drawState, boxes, setBoxes]);

  useEffect(() => {
    if (!editable) return;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [editable, onMouseMove, onMouseUp]);

  const onSvgMouseDown = (e) => {
    if (!editable || !addMode) return;
    const { x, y } = toImageCoords(e.clientX, e.clientY);
    setDrawState({ startX: x, startY: y, curX: x, curY: y });
  };

  const deleteBox = (i) => {
    const next = [...boxes];
    next.splice(i, 1);
    setBoxes(next);
  };

  return (
    <div className="space-y-3">
      {editable && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="add-box-btn"
            onClick={() => setAddMode((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${
              addMode
                ? "bg-indigo-500 text-white border-indigo-400"
                : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800"
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            {addMode ? "Draw box on image..." : "Add Finding"}
          </button>
          <span className="text-[10px] text-zinc-500 tracking-tight">
            Drag box untuk pindah · pojok kanan-bawah untuk resize · ✕ untuk hapus
          </span>
        </div>
      )}

      <div
        ref={wrapperRef}
        className="relative w-full bg-black rounded-xl overflow-hidden border border-zinc-800 flex items-center justify-center select-none"
        data-testid={testId}
        style={{
          aspectRatio: imageWidth && imageHeight ? `${imageWidth}/${imageHeight}` : "4/3",
          cursor: addMode ? "crosshair" : "default",
        }}
      >
        {imageSrc && (
          <>
            <img
              src={imageSrc}
              alt="Chest X-ray"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
            {imageWidth && imageHeight && (
              <svg
                ref={svgRef}
                viewBox={`0 0 ${imageWidth} ${imageHeight}`}
                preserveAspectRatio="xMidYMid meet"
                className="absolute inset-0 w-full h-full"
                onMouseDown={onSvgMouseDown}
                style={{ pointerEvents: editable ? "auto" : "none" }}
              >
                {boxes.map((b, i) => {
                  const color = boxColor(b.class, b.confidence ?? 0, colorMap);
                  const x = b.x - b.width / 2;
                  const y = b.y - b.height / 2;
                  const active = hoveredIndex === i;
                  const conf = b.confidence ?? 1;
                  const isRadiologist = b.source === "radiologist";
                  return (
                    <g key={i}>
                      <rect
                        x={x}
                        y={y}
                        width={b.width}
                        height={b.height}
                        fill={active ? `${color}22` : "transparent"}
                        stroke={color}
                        strokeWidth={active ? 5 : 3}
                        strokeDasharray={isRadiologist ? "0" : "8 4"}
                        onMouseDown={(e) => onMouseDown(e, i, "move")}
                        onMouseEnter={() => onHover && onHover(i)}
                        onMouseLeave={() => onHover && onHover(null)}
                        style={{ cursor: editable ? "move" : "pointer", pointerEvents: "auto" }}
                      />
                      {/* Label */}
                      <g style={{ pointerEvents: "none" }}>
                        <rect
                          x={x}
                          y={Math.max(0, y - 28)}
                          width={Math.max(90, (b.class + "").length * 11 + 40)}
                          height={24}
                          fill={color}
                          rx={3}
                        />
                        <text
                          x={x + 6}
                          y={Math.max(17, y - 10)}
                          fill="white"
                          fontSize={16}
                          fontFamily="Inter, sans-serif"
                          fontWeight={600}
                        >
                          {b.class} {conf < 1 ? `${(conf * 100).toFixed(0)}%` : "★"}
                        </text>
                      </g>
                      {editable && (
                        <>
                          {/* Delete button */}
                          <g
                            style={{ cursor: "pointer", pointerEvents: "auto" }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              deleteBox(i);
                            }}
                          >
                            <circle cx={x + b.width} cy={y} r={12} fill="#ef4444" />
                            <text
                              x={x + b.width}
                              y={y + 5}
                              fill="white"
                              fontSize={16}
                              fontWeight={700}
                              textAnchor="middle"
                            >
                              ×
                            </text>
                          </g>
                          {/* Resize handle (bottom-right) */}
                          <rect
                            x={x + b.width - 8}
                            y={y + b.height - 8}
                            width={16}
                            height={16}
                            fill={color}
                            stroke="white"
                            strokeWidth={2}
                            onMouseDown={(e) => onMouseDown(e, i, "resize-se")}
                            style={{ cursor: "nwse-resize", pointerEvents: "auto" }}
                          />
                        </>
                      )}
                    </g>
                  );
                })}
                {/* Drawing preview */}
                {drawState && (
                  <rect
                    x={Math.min(drawState.startX, drawState.curX)}
                    y={Math.min(drawState.startY, drawState.curY)}
                    width={Math.abs(drawState.curX - drawState.startX)}
                    height={Math.abs(drawState.curY - drawState.startY)}
                    fill="rgba(99,102,241,0.15)"
                    stroke="#6366f1"
                    strokeWidth={3}
                    strokeDasharray="6 4"
                  />
                )}
              </svg>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export { CLASSES, CLASS_COLORS };
